import { Params } from '@angular/router';
import { SortDirection } from '@iota/iota-ui-components';
import moment from 'moment';
import { ISO_DATE_FORMAT } from './date.util';

export interface QueryParamCodec<T> {
    readonly key: string;
    /** The page default: what a missing or unusable param restores. */
    readonly fallback?: T;
    /** `undefined` for a missing, empty or unrecognised value; `null` for an explicitly cleared one, which skips the fallback. */
    read(raw: string | undefined): T | null | undefined;
    /** `null` drops the param from the URL. */
    write(value: T): string | null;
}

export interface SortValue<T extends string> {
    field: T;
    ascending: boolean;
}

export function toSortDirection<T extends string>(sorting?: SortValue<T>): SortDirection {
    return sorting ? (sorting.ascending ? 'asc' : 'desc') : '';
}

export function toSortValue<T extends string>(field: T, direction?: SortDirection): SortValue<T>;
export function toSortValue<T extends string>(field: T | undefined, direction?: SortDirection): SortValue<T> | undefined;
export function toSortValue<T extends string>(field: T | undefined, direction?: SortDirection): SortValue<T> | undefined {
    return field ? { field, ascending: direction !== 'desc' } : undefined;
}

/** `-?` keeps the map exhaustive: a state field without a codec does not compile. */
export type QueryParamCodecs<T> = { readonly [K in keyof T]-?: QueryParamCodec<NonNullable<T[K]>> };

type UntypedCodecs = Record<string, QueryParamCodec<unknown>>;

/** A field whose param is missing or unusable gets its codec's fallback, so the result is the complete page state. */
export function readQueryParamState<T>(codecs: QueryParamCodecs<T>, params: Params): T {
    const state: Record<string, unknown> = {};

    for (const [field, codec] of Object.entries(codecs as UntypedCodecs)) {
        const value = codec.read(params[codec.key]);
        state[field] = value === null ? undefined : (value ?? codec.fallback);
    }

    return state as T;
}

export function toQueryParams<T>(codecs: QueryParamCodecs<T>, state: T): Params {
    const params: Params = {};
    const values = state as Record<string, unknown>;

    for (const [field, codec] of Object.entries(codecs as UntypedCodecs)) {
        params[codec.key] = codec.write(values[field]);
    }

    return params;
}

export function stringParam(key: string, fallback = ''): QueryParamCodec<string> {
    return {
        key,
        fallback,
        read: (raw) => trimmedOrUndefined(raw),
        write: (value) => {
            const trimmed = value?.trim() ?? '';
            return trimmed && trimmed !== fallback ? trimmed : null;
        }
    };
}

/** Negatives and non-integers are rejected: the number params are page indexes. */
export function numberParam(key: string, fallback?: number): QueryParamCodec<number> {
    return {
        key,
        fallback,
        read: (raw) => {
            const trimmed = trimmedOrUndefined(raw);
            if (trimmed === undefined) {
                return undefined;
            }

            const parsed = Number(trimmed);
            return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : undefined;
        },
        write: (value) => (Number.isFinite(value) && value !== fallback ? String(value) : null)
    };
}

export function enumParam<T extends string>(key: string, allowed: readonly T[], fallback?: T): QueryParamCodec<T> {
    return {
        key,
        fallback,
        read: (raw) => readEnum(raw, allowed),
        write: (value) => (value && value !== fallback ? value : null)
    };
}

export function enumListParam<T extends string>(key: string, allowed: readonly T[], fallback: readonly T[]): QueryParamCodec<readonly T[]> {
    return {
        key,
        fallback,
        read: (raw) => {
            const trimmed = trimmedOrUndefined(raw);
            if (trimmed === undefined) {
                return undefined;
            }

            const values = trimmed
                .split(',')
                .map((entry) => readEnum(entry, allowed))
                .filter((entry) => entry !== undefined);

            const unique = [...new Set(values)];
            return unique.length ? unique : undefined;
        },
        write: (value) => (value?.length && !haveSameMembers(value, fallback) ? value.join(',') : null)
    };
}

const CLEARED_DATE = 'none';

/** With a fallback every value is written, a cleared one as a token, so a restored URL never falls back to the relative default. */
export function dateParam(key: string, fallback?: string): QueryParamCodec<string> {
    return {
        key,
        fallback,
        read: (raw) => {
            const trimmed = trimmedOrUndefined(raw);
            if (trimmed === CLEARED_DATE) {
                return null;
            }

            return trimmed !== undefined && moment(trimmed, ISO_DATE_FORMAT, true).isValid() ? trimmed : undefined;
        },
        write: (value) => value || (fallback === undefined ? null : CLEARED_DATE)
    };
}

/** Without a fallback the page has no default sorting, so every sorting is written. */
export function sortParam<T extends string>(key: string, allowed: readonly T[], fallback?: SortValue<T>): QueryParamCodec<SortValue<T>> {
    return {
        key,
        fallback,
        read: (raw) => {
            const trimmed = trimmedOrUndefined(raw);
            if (trimmed === undefined) {
                return undefined;
            }

            const [rawField, rawDirection] = trimmed.split(':');
            const field = readEnum(rawField, allowed);
            if (field === undefined || (rawDirection !== 'asc' && rawDirection !== 'desc')) {
                return undefined;
            }

            return { field, ascending: rawDirection === 'asc' };
        },
        write: (value) => {
            if (!value?.field || (value.field === fallback?.field && value.ascending === fallback?.ascending)) {
                return null;
            }

            return `${value.field}:${value.ascending ? 'asc' : 'desc'}`;
        }
    };
}

function trimmedOrUndefined(raw: string | undefined): string | undefined {
    const trimmed = typeof raw === 'string' ? raw.trim() : '';
    return trimmed || undefined;
}

function readEnum<T extends string>(raw: string | undefined, allowed: readonly T[]): T | undefined {
    const trimmed = trimmedOrUndefined(raw);
    return allowed.includes(trimmed as T) ? (trimmed as T) : undefined;
}

function haveSameMembers<T>(values: readonly T[], other: readonly T[]): boolean {
    return values.length === other.length && values.every((value) => other.includes(value));
}
